import { Calendar } from "@fullcalendar/core"
import luxonPlugin from "@fullcalendar/luxon3"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import Choices from "choices.js"
import flatpickr from "flatpickr"
import { DateTime } from "luxon"
import Toastify from "toastify-js"
import "toastify-js/src/toastify.css"
import tippy, { hideAll } from "tippy.js"
import "tippy.js/dist/tippy.css"
import * as util from "./util"
import * as utillogin from "./utillogin"
import * as utildashboard from "./utildashboard"

// The Webflow head has too much CSS to fit this Inspectlet script so add here
/*
;(function () {
  window.__insp = window.__insp || []
  __insp.push(["wid", 983296258])
  var ldinsp = function () {
    if (typeof window.__inspld != "undefined") return
    window.__inspld = 1
    var insp = document.createElement("script")
    insp.type = "text/javascript"
    insp.async = true
    insp.id = "inspsync"
    insp.src =
      ("https:" == document.location.protocol ? "https" : "http") +
      "://cdn.inspectlet.com/inspectlet.js?wid=983296258&r=" +
      Math.floor(new Date().getTime() / 3600000)
    var x = document.getElementsByTagName("script")[0]
    x.parentNode.insertBefore(insp, x)
  }
  setTimeout(ldinsp, 0)
})()
*/

util.addStyleTag(`
  input.color-checkbox::before {
    box-shadow: none;
  }

  .calendar-event-payment {
    position: relative;
    padding-top: 12px;
  }

  .calendar-event-payment-badge {
    position: absolute;
    top: -4px;
    left: -4px;
    padding: 3px 6px;
    max-width: max(18px, 100%);
    border-radius: 999px;
    background: var(--calendar-event-badge-bg, #1aa979);
    color: #fff;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    box-shadow: 0 0 0 2px #fff;
    z-index: 3;
    pointer-events: none;
  }

  .calendar-event-payment-badge::after {
    content: attr(data-badge-label);
    font-size: 9px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
  }

  .calendar-event-payment .fc-event-main-frame {
    margin-left: 0;
  }

  @media (max-width: 768px) {
    .calendar-event-payment {
      padding-top: 12px;
    }

    .calendar-event-payment-badge::after {
      content: attr(data-badge-mobile-label);
    }
  }

`)

/* To locally debug this script via Webflow the best option I've found is to
add two scripts in Webflow:
<script src="http://localhost:3000/????.bundle.js"></script>
<script src="angry-boyd-1669aa.netlify.app/????.bundle.js" crossorigin="anonymous"></script>
The below check ensures that if localhost loads successfully the second
(production) script won't run.
Initially I tried to add the script dynamically in Webflow but that didn't
work - I needed it to run immediately but it wouldn't. */
if (typeof SharesyWebflow === "undefined") {
  util.initSentry()

  window.amplitudeOnLoad = function (instance) {
    util.amplitudeInit(instance, "Dashboard Calendar Viewed")
  }

  // Wrap everything with Revealing Module Pattern
  window.SharesyWebflow = (function () {
    var searchParams = util.getSearchParams()
    var calendar
    var calendarData = []
    var datesWithSessions = []
    var savedViewType = "timeGridWeek"
    var spaceData = []
    var selectedSessionId
    var fpStart // flatpickr
    var fpEnd
    var fpRecursUntil
    var fpCalendar
    var endDateSelected = false
    var blockError = {
      time: false,
      space: false,
      reason: false,
    }
    var blockStartChoices
    var blockEndChoices
    var isAdmin = false

    const palette = [
      "#1aa979", // Grass
      "#3f88e1", // Strong blue
      "#ffad00", // Cake
      "#e94662", // Soft Play
      "#04bfe9", // Light blue
      "#cb38c4", // Magenta
      "#955251", // Mauve
    ]

    const calendarSyncColor = "#fef1da"

    let spaceIDToChecklistIndex = {}

    function getColor(spaceIndex, colorType) {
      const colorIndex = spaceIndex % palette.length
      const percentage = [100, 87, 40][colorType]
      return `color-mix(in oklab, ${palette[colorIndex]} ${percentage}%, #000)`
    }

    async function initChecks() {
      isAdmin = await utillogin.getClerkUserIsAdmin()
      if (isAdmin) {
        utillogin.displayAdminNavItems()
        await initOrganisations()
        document.getElementById("add-booking").classList.remove("hide")
        document.getElementById("calendar-sync-button").classList.remove("hide")
        if (!document.getElementById("select-organisation").value) {
          document.getElementById("select-organisation").click()
        }
      } else {
        initSpaces(null)
        checkCalendarSyncStatus()
      }
    }

    async function checkCalendarSyncStatus() {
      let token = await utillogin.getClerkToken()
      let responseData
      try {
        responseData = await util.api
          .get(`cronofy-synced-spaces?token=${token}`)
          .json()
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Unknown Error Retrieving Synced Spaces")
      }
      if (responseData && responseData.spaces_without_syncs.length > 0) {
        document.getElementById("calendar-sync").classList.remove("hide")
      } else {
        document.getElementById("calendar-sync-button").classList.remove("hide")
      }
    }

    async function initCalendar(
      initialViewType = "timeGridWeek",
      initialDate = new Date()
    ) {
      // luxon format for Wed 5 Oct
      let dayHeaderFormatTimeGridWeek = "ccc d LLL"
      if (window.innerWidth < 1200) {
        // The class "linebreak-every-word-mobile" will also ensure "Wed 5"
        // splits over two lines on mobile.
        dayHeaderFormatTimeGridWeek = "ccc d"
      }
      calendar = new Calendar(document.getElementById("calendar"), {
        allDaySlot: false,
        datesSet: function (dateInfo) {
          if (dateInfo.view.type != savedViewType) {
            savedViewType = dateInfo.view.type
            if (savedViewType == "dayGridMonth") {
              calendar.setOption("height", "auto")
            } else {
              calendar.setOption("height", "100%")
              // The scrollTime is ignored without this second click
              if (savedViewType == "resourceTimeGridDay") {
                document.querySelector(".fc-resourceTimeGridDay-button").click()
              } else if (savedViewType == "timeGridWeek") {
                document.querySelector(".fc-timeGridWeek-button").click()
              }
            }
            // We display the times a space is not open as background events.
            // Because we only display them for some views, update events when
            // the view changes.
            calendar.refetchEvents()
          }
          if (fpCalendar) {
            if (
              fpCalendar.selectedDates.length == 0 ||
              fpCalendar.selectedDates[0] < dateInfo.view.currentStart ||
              fpCalendar.selectedDates[0] >= dateInfo.view.currentEnd
            ) {
              fpCalendar.setDate(dateInfo.view.currentStart)
            }
          }
        },
        dayMaxEventRows: 3,
        displayEventEnd: true,
        eventClick: async function (info) {
          if (info.event.extendedProps.type === "block") {
            // Hide all tippys - otherwise a tooltip displays over the
            // slide-over window on mobile
            hideAll()
            updateBlock(parseInt(info.event.id.split("-")[1].split("_")[0]))
          } else if (info.event.extendedProps.type === "session") {
            // Hide all tippys
            hideAll()
            selectedSessionId = parseInt(info.event.id.split("-")[1])
            utildashboard.openSessionSlideOver(selectedSessionId)
          } else if (
            info.event.extendedProps.type === "recurring_session" &&
            info.event.extendedProps.sessionId
          ) {
            // Open links in new tab
            if (isAdmin) {
              // Admins get taken to SB Admin in Retool
              window.open(
                `https://sharesy.retool.com/apps/019eac16-3a6a-11ed-81db-af9e81b0ce45/Emails%20%2B%20SB%20Admin#appointmentid=${info.event.extendedProps.sessionId}`,
                "_blank"
              )
            } else {
              // Venues are shown a link to the page that superbookers use so
              // they can remove sessions if they need to.
              try {
                let token = await utillogin.getClerkToken()
                let originalSession = await util.api
                  .get(
                    `get-session-detail?token=${token}&session_id=${info.event.extendedProps.sessionId}`
                  )
                  .json()
                Toastify({
                  text: `<a href="https://www.sharesy.com/recurring-admin?id=${originalSession.encrypted_session_id}" target="_blank" style="color:white">View all future sessions</a> for ${info.event.extendedProps.recurring_description}`,
                  escapeMarkup: false,
                  duration: 5000,
                  close: true,
                  gravity: "top",
                  position: "center",
                  stopOnFocus: true,
                  style: {
                    background: "#1aa979",
                  },
                }).showToast()
              } catch (error) {
                util.logToSentry(error)
                util.logAmplitudeEvent(
                  "Unknown Error Retrieving Recurring Booking"
                )
              }
            }
          }
        },
        eventColor: "#1aa979",
        eventDidMount: function (info) {
          if (info.event.display == "background") {
            tippy(info.el, {
              content: info.event.extendedProps.description,
              animation: "scale",
              duration: 0,
              placement: "right",
              touch: false,
            })
          } else {
            const unpaidInvoiceData = info.event.extendedProps.unpaidInvoiceData
            const tooltipHeader = unpaidInvoiceData
              ? unpaidInvoiceData.tooltip + "<br>"
              : ""
            tippy(info.el, {
              content:
                tooltipHeader +
                info.event.extendedProps.description +
                "<br>" +
                info.event.title,
              animation: "scale",
              duration: 0,
              allowHTML: true,
              placement: "right",
              touch: false,
            })
            if (info.event.extendedProps.borderStyle) {
              info.el.style.borderStyle = info.event.extendedProps.borderStyle
            }
            if (info.event.extendedProps.stripes) {
              info.el.style.background = `repeating-linear-gradient(
                  45deg,
                  ${info.event.extendedProps.stripes[0]},
                  ${info.event.extendedProps.stripes[0]} 14px,
                  ${info.event.extendedProps.stripes[1]} 14px,
                  ${info.event.extendedProps.stripes[1]} 19px
                )`
            }
            if (unpaidInvoiceData) {
              info.el.classList.add("calendar-event-payment")
              info.el.style.setProperty(
                "--calendar-event-badge-bg",
                unpaidInvoiceData.badgeColor
              )
              let badge = info.el.querySelector(".calendar-event-payment-badge")
              if (!badge) {
                badge = document.createElement("span")
                badge.className = "calendar-event-payment-badge"
                info.el.appendChild(badge)
              }
              badge.setAttribute(
                "data-badge-label",
                unpaidInvoiceData.label || ""
              )
              badge.setAttribute("data-badge-mobile-label", "£")
            }
          }
        },
        eventDisplay: "block",
        eventOrder: "rank,start,-duration,allDay,title",
        events: getEvents,
        eventTimeFormat: {
          hour: "2-digit",
          minute: "2-digit",
          meridiem: false,
          hour12: false,
        },
        firstDay: 1,
        headerToolbar: {
          left: "today prev,next title",
          right: "dayGridMonth,timeGridWeek,resourceTimeGridDay",
        },
        height: "100%",
        initialDate: initialDate,
        initialView: initialViewType,
        locale: "en-GB",
        navLinkDayClick: "resourceTimeGridDay",
        navLinks: true,
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          luxonPlugin,
          resourceTimeGridPlugin,
          interactionPlugin,
        ],
        resources: getResources,
        resourceOrder: "sortOrder,title",
        schedulerLicenseKey: "CC-Attribution-NonCommercial-NoDerivatives",
        scrollTime: "08:00:00",
        timeZone: util.TIMEZONE,
        views: {
          resourceTimeGridDay: {
            titleFormat: "ccc d LLL yyyy",
            dayHeaderFormat: "ccc d LLL",
            allDaySlot: false,
            selectable: true,
            select: function (info) {
              openAddBlockSlideOver(
                null,
                info.startStr,
                info.endStr,
                info.resource.id
              )
            },
          },
          timeGridWeek: {
            dayHeaderFormat: dayHeaderFormatTimeGridWeek,
            dayHeaderClassNames: "linebreak-every-word-mobile",
            selectable: true,
            select: function (info) {
              openAddBlockSlideOver(null, info.startStr, info.endStr)
            },
          },
          dayGridMonth: {
            selectable: true,
            select: function (info) {
              // Don't use endStr - it will be midnight next day
              openAddBlockSlideOver(null, info.startStr, info.startStr)
            },
          },
        },
      })
      calendar.render()
      //window.globalCal = calendar

      if (!fpCalendar) {
        fpCalendar = flatpickr("#inline-flatpickr-calendar", {
          appendTo: document.getElementById("inline-flatpickr-calendar"),
          inline: true,
          enableTime: false,
          wrap: true,
          defaultDate: "today",
          disableMobile: "true",
          monthSelectorType: "static",
          locale: {
            firstDayOfWeek: 1, // start week on Monday
            weekdays: {
              shorthand: ["S", "M", "T", "W", "T", "F", "S"],
              longhand: [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ],
            },
          },
          onChange: function (selectedDates, dateStr, instance) {
            if (selectedDates.length > 0) {
              calendar.gotoDate(selectedDates[0])
              util.logAmplitudeEvent("Select Mini Calendar Date")
            }
          },
          onDayCreate: function (dObj, dStr, fp, dayElem) {
            if (
              datesWithSessions.indexOf(
                DateTime.fromJSDate(dayElem.dateObj, {
                  zone: util.TIMEZONE,
                }).toISODate()
              ) !== -1
            ) {
              dayElem.classList.remove("muted")
              dayElem.classList.add("bold")
            } else {
              dayElem.classList.add("muted")
              dayElem.classList.remove("bold")
            }
          },
        })
      }
    }

    async function updateBlock(blockId) {
      openAddBlockSlideOver(blockId)
    }

    async function copyBlock(blockId) {
      closeAddBlockSlideOver()
      openAddBlockSlideOver(blockId, null, null, null, true)
    }

    function getSelectedSpaceIds(prefix = "") {
      let selectedSpaceIds = []
      let splitPosition = 2
      if (prefix.includes("-")) {
        splitPosition = 3
      }
      document
        .querySelectorAll('input[id^="' + prefix + 'space-checkbox-"]')
        .forEach(function (d) {
          if (d.checked) {
            selectedSpaceIds.push(parseInt(d.id.split("-")[splitPosition]))
          }
        })
      return selectedSpaceIds
    }

    function getEvents(info, successCallback, failureCallback) {
      let selectedSpaceIds = getSelectedSpaceIds("nav-")
      let allEvents = calendarData
        .filter(function (d) {
          return d.spaces
            .map((space) => space.space_id)
            .some((item) => selectedSpaceIds.includes(item))
        })
        .map(function (d) {
          let end_utc = DateTime.fromISO(d.end_utc, { zone: util.TIMEZONE })
          if (end_utc.hour == 0 && end_utc.minute == 0) {
            end_utc = end_utc.minus({ minutes: 1 })
          }

          let event = {
            id: d.id,
            start: d.start_utc,
            end: end_utc.toUTC().toISO(),
            title: d.title_what,
            description: d.title_when_where,
            resourceIds: d.spaces.map((space) => "" + space.space_id),
            extendedProps: {
              type: d.type,
              rank: d.rank,
            },
          }
          if (d.type === "session") {
            const c = getColor(spaceIDToChecklistIndex[d.spaces[0].space_id], 0)
            event.color = c

            if (d.confirmed === false) {
              event.borderStyle = "solid"
              event.borderColor = c
              event.textColor = c
              event.color = "#fff"
              if (event.start > DateTime.now().toISO()) {
                event.title = "TO CONFIRM: " + event.title
              }
            } else {
              const unpaidInvoiceData = getUnpaidInvoiceData(d.invoice, c)
              if (unpaidInvoiceData) {
                event.extendedProps.unpaidInvoiceData = unpaidInvoiceData
              }
            }
          } else if (d.type === "recurring_session") {
            const primary = getColor(
              spaceIDToChecklistIndex[d.spaces[0].space_id],
              0
            )
            const secondary = getColor(
              spaceIDToChecklistIndex[d.spaces[0].space_id],
              1
            )
            event.textColor = "#fff"
            event.stripes = [primary, secondary]
            event.color = primary

            event.extendedProps.sessionId = d.original_session_id
            event.extendedProps.recurring_description = d.description
          } else if (d.type === "block") {
            event.textColor = "#1d1d1d"
            event.color = "#ebebeb"
          } else if (d.type === "calendar_sync_block") {
            event.textColor = "#1d1d1d"
            event.color = calendarSyncColor
          }
          return event
        })
      if (
        savedViewType == "resourceTimeGridDay" ||
        (savedViewType == "timeGridWeek" && selectedSpaceIds.length == 1)
      ) {
        /* We don't want to show background events on the week view if multiple
        spaces are selected. E.g. if one space is open 06-13 and another is
        open 14-24, we would get two background events showing (13-24 and 06-14
        instead of 13-14).

        TODO: if savedViewType == "timeGridWeek" && selectedSpaceIds.length > 1
        calculate intersecting background events and just show them.
        E.g. if inverse standard hours are:
          Main Hall: 6-7, 8.30-15.30, 18-6nextday
          Small Hall: 6-18, 22-6nextday
          Intersection = 6-7, 8.30-15.30, 22-6nextday
        A possible approach:
        - get single list of inverse standard + override hours
        - filter down to current date
        - compare first two spaces, check intersection of each interval in space 1 against space 2 intervals
        - save the intersecting intervals
        - compare these saved intervals against space 3 intervals, etc
        Could use https://moment.github.io/luxon/api-docs/index.html#intervalintersection. */
        successCallback(allEvents.concat(getBackgroundEvents(selectedSpaceIds)))
      } else {
        successCallback(allEvents)
      }
    }

    function getUnpaidInvoiceData(invoice, spaceColour) {
      if (!invoice || !invoice.unpaid_invoice) {
        return null
      }
      const unpaidValue = invoice.unpaid_invoice
      const dueDateLabel = formatInvoiceDueDate(invoice.due_date)

      if (unpaidValue.startsWith("Unpaid")) {
        return {
          badgeColor: spaceColour,
          label: dueDateLabel ? `Due ${dueDateLabel}` : "Unpaid",
          tooltip: dueDateLabel
            ? `Invoice unpaid - due ${dueDateLabel}`
            : "Invoice unpaid",
        }
      }

      if (unpaidValue.startsWith("Pending")) {
        return {
          badgeColor: spaceColour,
          label: "Pending",
          tooltip: "Direct debit payment in progress",
        }
      }

      if (unpaidValue.startsWith("Overdue")) {
        return {
          badgeColor: "#1d1d1d",
          label: "Overdue",
          tooltip: dueDateLabel
            ? `Invoice overdue - due ${dueDateLabel}`
            : "Invoice overdue",
        }
      }

      return null
    }

    function formatInvoiceDueDate(dueDateString) {
      if (!dueDateString) {
        return null
      }
      const parsed = DateTime.fromISO(dueDateString)
      if (!parsed.isValid) {
        return null
      }
      return parsed.toFormat("d LLL")
    }

    /**
     * FullCalendar's businessHours isn't able to handle split hours or
     * overrides for a given date. So we instead add 'background' events. We
     * add them for standard hours and override hours. Because we add an event
     * for the times the space isn't available, we use the "inverse" hours
     * returned by the API.
     * @param {Array} selectedSpaceIds the spaces selected in the left nav.
     * @returns {Array}
     */
    function getBackgroundEvents(selectedSpaceIds) {
      let standardBackgroundEvents = []
      // Start from this Monday to avoid the week view looking weird
      let mostRecentMonday = DateTime.local().startOf("week")
      let currentDate = mostRecentMonday
      // 13 months is arbitrary - long enough that users are unlikely to look beyond
      let endDate = currentDate.plus({ months: 13 })
      while (currentDate <= endDate) {
        spaceData
          .filter(function (d) {
            return selectedSpaceIds.includes(d.space_id)
          })
          .forEach(function (space) {
            let overrideEvents = []
            if (space.inverse_override_hours) {
              space.inverse_override_hours
                .filter(function (h) {
                  return (
                    h.start_datetime.split("T")[0] == currentDate.toISODate()
                  )
                })
                .map(function (h) {
                  overrideEvents.push({
                    start: h.start_datetime,
                    end: h.end_datetime,
                    resourceIds: ["" + space.space_id],
                    display: "background",
                    backgroundColor: "#e8e8e8",
                    description:
                      "Closed (override hours for " +
                      DateTime.fromISO(h.start_datetime)
                        .setZone(util.TIMEZONE)
                        .toFormat("d LLL") +
                      ")",
                  })
                })
            }
            if (overrideEvents.length > 0) {
              standardBackgroundEvents =
                standardBackgroundEvents.concat(overrideEvents)
              return
            }
            const weekday = currentDate.toFormat("cccc")
            if (
              space.inverse_standard_hours &&
              space.inverse_standard_hours[weekday]
            ) {
              space.inverse_standard_hours[weekday].map(function (h) {
                let endTime
                if (h.is_end_time_next_day) {
                  endTime =
                    currentDate.plus({ days: 1 }).toISODate() +
                    "T" +
                    h.end_time_local
                } else {
                  endTime = currentDate.toISODate() + "T" + h.end_time_local
                }
                standardBackgroundEvents.push({
                  start: currentDate.toISODate() + "T" + h.start_time_local,
                  end: endTime,
                  resourceIds: ["" + space.space_id],
                  display: "background",
                  backgroundColor: "#e8e8e8",
                  description:
                    "Closed (" +
                    currentDate.toFormat("cccc") +
                    " opening hours)",
                })
              })
            }
          })
        currentDate = currentDate.plus({ days: 1 })
      }
      return standardBackgroundEvents
    }

    function getResources(fetchInfo, successCallback, failureCallback) {
      successCallback(getResourceData())
    }

    function getResourceData() {
      let selectedSpaceIds = getSelectedSpaceIds("nav-")
      return spaceData
        .filter(function (d) {
          return selectedSpaceIds.includes(d.space_id)
        })
        .map(function (d) {
          return {
            id: "" + d.space_id,
            title: d.space_display_name,
            sortOrder: d.sort_order,
          }
        })
    }

    async function initOrganisations() {
      let token = await utillogin.getClerkToken()
      let organisationData
      try {
        organisationData = await util.api
          .get(`get-organisations-token?token=${token}`, {
            retry: { limit: 4 },
          })
          .json()
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Unknown Error Retrieving Organisations")
      }
      const organisationSelect = document.getElementById("select-organisation")
      let organisationChoices = new Choices(organisationSelect, {
        itemSelectText: "",
        allowHTML: false,
        searchResultLimit: 10,
        choices: organisationData.map(function (d) {
          return {
            value: d.id,
            label: d.name,
          }
        }),
      })
      document.getElementById("organisation-filter").classList.remove("hide")
      organisationSelect.addEventListener(
        "change",
        async function (event) {
          let organisationId = parseInt(event.target.value)
          initSpaces(organisationId)
          util.updateUrlParam("organisation", organisationId, false, false)
          util.addUrlParamToLink(
            document.getElementById("calendar-sync-button"),
            "organisation",
            organisationId
          )
          util.addUrlParamToLink(
            document.getElementById("block-calendar-sync-link"),
            "organisation",
            organisationId
          )
        },
        false
      )
      if (searchParams.has("organisation")) {
        let organisationId = parseInt(searchParams.get("organisation"))
        organisationChoices.setChoiceByValue(organisationId)
        initSpaces(organisationId)
        util.addUrlParamToLink(
          document.getElementById("calendar-sync-button"),
          "organisation",
          organisationId
        )
        util.addUrlParamToLink(
          document.getElementById("block-calendar-sync-link"),
          "organisation",
          organisationId
        )
      }
    }

    async function initSpaces(organisationId) {
      let token = await utillogin.getClerkToken()
      let endpoint = `get-spaces-with-standard-hours?token=${token}`
      if (organisationId) {
        endpoint += `&organisation_id=${organisationId}`
      }
      try {
        spaceData = await util.api.get(endpoint, { retry: { limit: 4 } }).json()
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Unknown Error Retrieving Spaces")
      }
      displaySpaceCheckboxes("nav-", "Calendar Nav")
      document.getElementById("add-block").classList.remove("hide")
      loadCalendarData(organisationId, true)
    }

    /**
     * Load the calendar data, then update the FullCalendar and mini calendars.
     * @param {number} organisationId
     * @param {boolean} loadForNewOrganisation If true, load the current
     * calendar data first for performance reasons, and recreate the
     * FullCalendar.
     * @returns {Array}
     */
    async function loadCalendarData(organisationId, loadForNewOrganisation) {
      let token = await utillogin.getClerkToken()
      let endpoint = `get-sessions-and-blocks-token?token=${token}`
      if (organisationId) {
        endpoint += `&organisation_id=${organisationId}`
      }
      if (loadForNewOrganisation) {
        try {
          // Retrieving all data is slow, so initially just get data for current
          // month / next 2 weeks
          calendarData = await util.api
            .get(endpoint + "&nearest_only=true", { retry: { limit: 4 } })
            .json()
        } catch (error) {
          util.logToSentry(error)
          util.logAmplitudeEvent("Unknown Error Retrieving Sessions and Blocks")
        }
        updateCalendars(true)
      }

      try {
        // Retrieve all calendar data
        calendarData = await util.api
          .get(endpoint, { retry: { limit: 4 } })
          .json()
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Unknown Error Retrieving Sessions and Blocks")
      }
      updateCalendars(false)
    }

    /**
     * Update the mini calendar and the main calendar.
     * @param {boolean} shouldRecreateCalendar Whether to destroy then create
     * the calendar. This is necessary if business hours change (because
     * the selected spaces have changed).
     */
    function updateCalendars(shouldRecreateCalendar) {
      updateDatesWithSessions()
      if (calendar) {
        if (shouldRecreateCalendar) {
          recreateCalendar()
        } else {
          calendar.refetchEvents()
        }
        // If the datesWithSessions have changed then do the minimal thing to
        // trigger onDayCreate so the formatting in the mini calendar is
        // updated (e.g. bold all the valid dates).
        fpCalendar.changeMonth(0)
      } else {
        initCalendar()
      }
    }

    /**
     * Update the list of valid dates used by the mini calendar.
     */
    function updateDatesWithSessions() {
      let selectedSpaceIds = getSelectedSpaceIds("nav-")
      datesWithSessions = calendarData
        .filter(function (d) {
          return d.spaces
            .map((space) => space.space_id)
            .some((item) => selectedSpaceIds.includes(item))
        })
        .filter(function (d) {
          return d.type === "session" || d.type === "recurring_session"
        })
        .map(function (d) {
          return d.start_utc.split("T")[0]
        })
        .filter(function (d, i, self) {
          return self.indexOf(d) === i
        })
    }

    function recreateCalendar() {
      if (calendar) {
        let viewType = calendar.view.type
        calendar.destroy()
        if (fpCalendar && fpCalendar.selectedDates.length > 0) {
          initCalendar(viewType, fpCalendar.selectedDates[0])
        } else {
          initCalendar(viewType)
        }
      } else {
        initCalendar()
      }
    }

    async function openAddBlockSlideOver(
      blockId,
      startISOStr,
      endISOStr,
      spaceId,
      copyBlock = false
    ) {
      document.getElementById("slide-over-container").classList.remove("hide")
      document.getElementById("slide-over-container").classList.add("show")
      document.getElementById("delete-block").innerText = "Delete block"
      document.getElementById("delete-block").disabled = false
      document.getElementById("admin-add-booking").classList.add("hide")
      document.getElementById("add-block-error").style.display = "none"
      endDateSelected = false
      // Check if flatpickr already exists - if so assume this has been run before
      if (!fpStart) {
        fpStart = flatpickr("#flatpickr-start", {
          enableTime: false,
          wrap: true,
          defaultDate: "today",
          disableMobile: "true",
          monthSelectorType: "static",
          locale: {
            firstDayOfWeek: 1, // start week on Monday
          },
          onChange: function (selectedDates, dateStr, instance) {
            if (selectedDates.length > 0) {
              document.getElementById("start-date-text").innerText =
                selectedDates[0].toLocaleDateString("en-GB", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              if (
                !endDateSelected ||
                fpEnd.selectedDates[0] < selectedDates[0]
              ) {
                setFlatpickrDate(selectedDates[0], fpEnd, "end-date-text")
              }
              updateMinRecursUntilDate(selectedDates[0])
              util.logAmplitudeEvent("Select Start Date (Add Block)")
            }
          },
        })
        fpEnd = flatpickr("#flatpickr-end", {
          enableTime: false,
          wrap: true,
          defaultDate: "today",
          disableMobile: "true",
          monthSelectorType: "static",
          locale: {
            firstDayOfWeek: 1, // start week on Monday
          },
          onChange: function (selectedDates, dateStr, instance) {
            if (selectedDates.length > 0) {
              document.getElementById("end-date-text").innerText =
                selectedDates[0].toLocaleDateString("en-GB", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              endDateSelected = true
              // Hide weekly recurring if duration is 5 days or more
              if (
                (selectedDates[0] - fpStart.selectedDates[0]) /
                  (1000 * 60 * 60 * 24) >=
                5
              ) {
                document
                  .getElementById("recurring-block-section")
                  .classList.add("hide")
              } else {
                document
                  .getElementById("recurring-block-section")
                  .classList.remove("hide")
              }
              util.logAmplitudeEvent("Select End Date (Add Block)")
            }
          },
        })
        fpRecursUntil = flatpickr("#flatpickr-recurs-until", {
          enableTime: false,
          wrap: true,
          disableMobile: "true",
          monthSelectorType: "static",
          locale: {
            firstDayOfWeek: 1, // start week on Monday
          },
          onChange: function (selectedDates, dateStr, instance) {
            if (selectedDates.length > 0) {
              document.getElementById("recurs-until-date-text").innerText =
                selectedDates[0].toLocaleDateString("en-GB", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              util.logAmplitudeEvent("Select Recurs Until Date")
            }
            hideBlockError("recurring")
          },
        })
        let startTimes = util.generateTimes(0, 0, true)
        startTimes.pop()
        let endTimes = util.generateTimes(0, 0, true)
        endTimes.shift()
        blockStartChoices = generateChoices(
          document.getElementById("start-time"),
          startTimes
        )
        blockEndChoices = generateChoices(
          document.getElementById("end-time"),
          endTimes
        )
      }
      document
        .getElementById("recurring-block-section")
        .classList.remove("hide")
      document.getElementById("weekly-recurring-checkbox").checked = false
      document.getElementById("recurs-until-section").classList.add("hide")
      setFlatpickrDate(null, fpRecursUntil, "recurs-until-date-text")
      displaySpaceCheckboxes("", "Add Block")
      let allDaySelected = true
      let allSpacesChecked = true
      if (blockId) {
        let token = await utillogin.getClerkToken()
        try {
          let blockData = await util.api
            .get(`get-block-token?token=${token}&block_id=${blockId}`, {
              retry: { limit: 4 },
            })
            .json()
          let startDateTime = DateTime.fromISO(blockData[0].start_utc, {
            zone: util.TIMEZONE,
          })
          let endDateTime = DateTime.fromISO(blockData[0].end_utc, {
            zone: util.TIMEZONE,
          })
          if (startDateTime < DateTime.now()) {
            fpStart.set("minDate", startDateTime.toISODate())
          }
          if (endDateTime.hour === 0 && endDateTime.minute === 0) {
            endDateTime = endDateTime.minus({ days: 1 })
          }
          if (endDateTime < DateTime.now()) {
            fpEnd.set("minDate", endDateTime.toISODate())
          }
          setFlatpickrDate(
            startDateTime.toFormat("yyyy-MM-dd"),
            fpStart,
            "start-date-text"
          )
          setFlatpickrDate(
            endDateTime.toFormat("yyyy-MM-dd"),
            fpEnd,
            "end-date-text"
          )
          if (
            startDateTime.hour === 0 &&
            startDateTime.minute === 0 &&
            endDateTime.hour === 0 &&
            endDateTime.minute === 0
          ) {
            allDaySelected = true
          } else {
            blockStartChoices.setChoiceByValue(startDateTime.toFormat("HH:mm"))
            blockEndChoices.setChoiceByValue(endDateTime.toFormat("HH:mm"))
            allDaySelected = false
          }
          if (blockData[0].recurring_frequency) {
            document.getElementById("weekly-recurring-checkbox").checked = true
            document
              .getElementById("recurs-until-section")
              .classList.remove("hide")
            setFlatpickrDate(
              blockData[0].recurs_until,
              fpRecursUntil,
              "recurs-until-date-text"
            )
          }
          document.getElementById("block-reason").value = blockData[0].note
          // Check the spaces that have an id matching blockData[0].spaces.space_id
          document
            .querySelectorAll('input[id^="space-checkbox-"]')
            .forEach(function (d) {
              if (
                blockData[0].spaces
                  .map((space) => space.space_id)
                  .includes(parseInt(d.id.split("-")[2]))
              ) {
                d.checked = true
                d.dispatchEvent(new Event("sharesy-color-update"))
              } else {
                d.checked = false
                d.dispatchEvent(new Event("sharesy-color-update"))
                allSpacesChecked = false
              }
            })
          if (copyBlock) {
            document.getElementById("update-buttons").classList.add("hide")
            document.getElementById("add-block-button").classList.remove("hide")
          } else {
            document.getElementById("update-block").dataset.blockId = blockId
            document.getElementById("update-buttons").classList.remove("hide")
            document.getElementById("add-block-button").classList.add("hide")
          }
        } catch (error) {
          util.logToSentry(error)
          util.logAmplitudeEvent("Unknown Error Retrieving Organisations")
        }
      } else {
        document.getElementById("block-reason").value = ""
        document
          .querySelectorAll('input[id^="space-checkbox-"]')
          .forEach(function (d) {
            d.checked = false
            d.dispatchEvent(new Event("sharesy-color-update"))
          })
        document.getElementById("all-spaces-checkbox").checked = false
        document.getElementById("update-buttons").classList.add("hide")
        document.getElementById("add-block-button").classList.remove("hide")
        let newBookingHref =
          "https://sharesy.retool.com/apps/abfc35a0-71c0-11ee-bd2d-fb126ee55298/New%20Booking?"
        if (startISOStr) {
          let startDateTime = DateTime.fromISO(startISOStr).setZone(
            util.TIMEZONE
          )
          let endDateTime = DateTime.fromISO(endISOStr).setZone(util.TIMEZONE)
          setFlatpickrDate(startDateTime.toJSDate(), fpStart, "start-date-text")
          setFlatpickrDate(endDateTime.toJSDate(), fpEnd, "end-date-text")
          if (startISOStr.length > 10) {
            blockStartChoices.setChoiceByValue(startDateTime.toFormat("HH:mm"))
            blockEndChoices.setChoiceByValue(endDateTime.toFormat("HH:mm"))
          } else {
            blockStartChoices.setChoiceByValue("")
            blockEndChoices.setChoiceByValue("")
          }
          allDaySelected = false
          // encode the start and end strings
          newBookingHref += `date=${startDateTime.toFormat(
            "yyyy-MM-dd"
          )}&start=${encodeURIComponent(
            startDateTime.toFormat("HH:mm")
          )}&end=${encodeURIComponent(endDateTime.toFormat("HH:mm"))}`
        } else {
          setFlatpickrDate("today", fpStart, "start-date-text")
          setFlatpickrDate("today", fpEnd, "end-date-text")
        }
        let effectiveSpaceId = spaceId
        if (!spaceId && spaceData.length === 1) {
          effectiveSpaceId = spaceData[0].space_id
        }
        let effectiveSpaceIds = []
        if (effectiveSpaceId) {
          effectiveSpaceIds = [effectiveSpaceId]
          allSpacesChecked = false
        } else {
          // Add to effectiveSpaceIds for each checked nav-space-checkbox-
          document
            .querySelectorAll('input[id^="nav-space-checkbox-"]')
            .forEach(function (d) {
              if (d.checked) {
                effectiveSpaceIds.push(parseInt(d.id.split("-")[3]))
              } else {
                allSpacesChecked = false
              }
            })
        }
        for (let i = 0; i < effectiveSpaceIds.length; i++) {
          if (i == 0) {
            newBookingHref += `&space=${effectiveSpaceIds[i]}`
          }
          const el = document.getElementById(
            `space-checkbox-${effectiveSpaceIds[i]}`
          )
          el.checked = true
          el.dispatchEvent(new Event("sharesy-color-update"))
        }
        if (document.getElementById("select-organisation").value) {
          newBookingHref += `&org=${util.getSearchParams().get("organisation")}`
          document.getElementById("admin-add-booking-link").href =
            newBookingHref
          document.getElementById("admin-add-booking").classList.remove("hide")
        }
      }
      document.getElementById("all-spaces-checkbox").checked = allSpacesChecked
      if (
        document.getElementById("all-day-checkbox").checked !== allDaySelected
      ) {
        document.getElementById("all-day-checkbox").click()
      }
      document.getElementById("add-block-slide-over").classList.remove("hide")
    }

    function updateMinRecursUntilDate(selectedDate) {
      // Force user to end recurring block at least 1 week after start
      let dateOneWeekLater = new Date(selectedDate)
      dateOneWeekLater.setDate(dateOneWeekLater.getDate() + 7)
      fpRecursUntil.set("minDate", dateOneWeekLater)
      if (!fpRecursUntil.selectedDates) {
        // If setting the minimum has unselected the date, update the text
        setFlatpickrDate(null, fpRecursUntil, "recurs-until-date-text")
      }
    }

    function closeAddBlockSlideOver() {
      document.getElementById("add-block-slide-over").classList.add("hide")
      document.getElementById("slide-over-container").classList.remove("show")
      document.getElementById("slide-over-container").classList.add("hide")
    }

    function setFlatpickrDate(date, fp, displayTextId) {
      fp.setDate(date)
      // Check fp.selectedDates because it could fail to set because e.g. we
      // tried to set a date before the minDate.
      if (date && fp.selectedDates[0]) {
        document.getElementById(displayTextId).innerText =
          fp.selectedDates[0].toLocaleDateString("en-GB", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
          })
      } else {
        document.getElementById(displayTextId).innerText = "Choose date"
      }
      if (fp === fpStart) {
        updateMinRecursUntilDate(date)
      }
    }

    function generateChoices(element, times) {
      let choices = new Choices(element, {
        itemSelectText: "",
        shouldSort: false,
        allowHTML: false,
        fuseOptions: {
          threshold: 0.0,
          ignoreLocation: true,
        },
        choices: times.map(function (d) {
          return {
            value: d,
            label: d,
          }
        }),
      })
      return choices
    }

    function setCheckboxColor(checkbox, spaceIndex) {
      checkbox.classList.add("color-checkbox")
      if (checkbox.checked) {
        checkbox.style["background"] = getColor(spaceIndex, 0)
        checkbox.style["border"] = ""
      } else {
        checkbox.style["background"] = ""
        checkbox.style["border"] = "1px solid " + getColor(spaceIndex, 0)
      }
    }

    function displaySpaceCheckboxes(prefix, amplitudeSectionName) {
      document
        .getElementById(prefix + "all-spaces-checkbox")
        .parentElement.classList.remove("bold")
      let divSpaces = document.getElementById(prefix + "spaces")
      // Remove any existing spaces from previous modal load
      while (divSpaces.childNodes.length > 1) {
        divSpaces.removeChild(divSpaces.lastChild)
      }
      let divSpace = divSpaces.firstChild
      divSpace.style["display"] = "" // Undo hiding of "All spaces" from previous executions

      spaceData.sort((a, b) => {
        if (a.sort_order === b.sort_order) {
          return a.space_display_name.localeCompare(b.space_display_name)
        }
        if (a.sort_order === null) {
          return 1
        }
        if (b.sort_order === null) {
          return -1
        }
        return a.sort_order - b.sort_order
      })

      spaceData.forEach(function (space, index) {
        spaceIDToChecklistIndex[space.space_id] = index
        let newSpace = divSpace.cloneNode(true)
        setCheckboxColor(newSpace.querySelector("input"), index)
        newSpace
          .querySelector("input")
          .setAttribute("id", prefix + "space-checkbox-" + space.space_id)
        newSpace.querySelector(".dashboard-checkbox-text").innerText =
          space.space_display_name

        const checkbox = newSpace.querySelector("input")
        checkbox.addEventListener("sharesy-color-update", function (e) {
          // NOTE: the "change" event only gets triggered by user input, so we need a
          // custom event to update the checkbox color when checkboxes are
          // are checked or unchecked programmatically, without triggering analytics
          // events etc.
          setCheckboxColor(e.target, index)
        })

        checkbox.addEventListener("change", function () {
          setCheckboxColor(this, index)
          if (this.checked) {
            let allChecked = true
            document
              .querySelectorAll(`input[id^="${prefix}space-checkbox-"]`)
              .forEach(function (d) {
                if (!d.checked) {
                  allChecked = false
                }
              })
            if (allChecked) {
              document.getElementById(
                prefix + "all-spaces-checkbox"
              ).checked = true
            }
            if (prefix == "") {
              hideBlockError("space")
            }
            util.logAmplitudeEvent(`Space Selected (${amplitudeSectionName})`, {
              "Space ID": space.space_id,
              "Space Name": space.space_name,
            })
          } else {
            document.getElementById(
              prefix + "all-spaces-checkbox"
            ).checked = false
            util.logAmplitudeEvent(
              `Space Unselected (${amplitudeSectionName})`,
              {
                "Space ID": space.space_id,
                "Space Name": space.space_name,
              }
            )
          }
          if (prefix == "nav-" && calendarData) {
            // If the space changes we destroy the calendar to reset
            // business hours. Don't update before calendarData loads.
            updateCalendars(true)
          }
        })
        newSpace.classList.remove("hide")
        divSpaces.appendChild(newSpace)
      })
      document
        .getElementById(prefix + "all-spaces-checkbox")
        .parentElement.classList.add("bold")

      if (spaceData.length < 2) {
        // Hide "All spaces"
        divSpace.style["display"] = "none"
      }
    }

    function displayBlockError(type, message, amplitudeErrorProperty) {
      blockError[type] = true
      let errorMessage = document.getElementById("add-block-error")
      errorMessage.innerText = message
      errorMessage.style.display = "block"
      util.logAmplitudeEvent("Save Block Error", {
        Error: amplitudeErrorProperty,
      })
    }

    function hideBlockError(type) {
      if (!blockError[type]) {
        return
      }
      blockError[type] = false
      document.getElementById("add-block-error").style.display = "none"
    }

    async function addBlock(isUpdate, button) {
      // Flatpickr will return a JS date in local time. If that timezone is
      // significantly different to the venue timezone then startDateTime can
      // end up on a different day.
      let fpStartISO = util.jsDateToIso(fpStart.selectedDates[0])
      let fpEndISO = util.jsDateToIso(fpEnd.selectedDates[0])
      let startDateTime
      let endDateTime
      if (document.getElementById("all-day-checkbox").checked) {
        startDateTime = DateTime.fromISO(fpStartISO, {
          zone: util.TIMEZONE,
        }).set({ hours: 0, minutes: 0 })
        endDateTime = DateTime.fromISO(fpEndISO, {
          zone: util.TIMEZONE,
        })
          .plus({ days: 1 })
          .set({ hours: 0, minutes: 0 })
      } else {
        if (
          !document.getElementById("start-time").value ||
          !document.getElementById("end-time").value
        ) {
          displayBlockError(
            "time",
            "You must choose a start and end time.",
            "Missing Time"
          )
          return
        }
        startDateTime = DateTime.fromISO(fpStartISO, {
          zone: util.TIMEZONE,
        }).set({
          hours: parseInt(
            document.getElementById("start-time").value.split(":")[0]
          ),
          minutes: parseInt(
            document.getElementById("start-time").value.split(":")[1]
          ),
        })
        endDateTime = DateTime.fromISO(fpEndISO, {
          zone: util.TIMEZONE,
        }).set({
          hours: parseInt(
            document.getElementById("end-time").value.split(":")[0]
          ),
          minutes: parseInt(
            document.getElementById("end-time").value.split(":")[1]
          ),
        })
        if (document.getElementById("end-time").value === "00:00") {
          endDateTime = endDateTime.plus({ days: 1 })
        }
        if (endDateTime <= startDateTime) {
          displayBlockError(
            "time",
            "The end time must be after the start time.",
            "Invalid Time"
          )
          return
        }
      }
      if (
        document.getElementById("weekly-recurring-checkbox").checked &&
        fpRecursUntil.selectedDates.length === 0
      ) {
        displayBlockError(
          "recurring",
          "You must select a date for the block to repeat until. If you want to block time indefinitely you should change your Opening Hours instead.",
          "Invalid Time"
        )
        return
      }
      let selectedSpaceIds = getSelectedSpaceIds()
      if (selectedSpaceIds.length === 0) {
        displayBlockError(
          "space",
          "You must select at least one space.",
          "Space Not Selected"
        )
        return
      }
      let reason = document.getElementById("block-reason").value.trim()
      if (reason.length === 0) {
        displayBlockError(
          "reason",
          "You must enter a reason.",
          "Reason Not Entered"
        )
        return
      }
      let recurringFrequency = null
      let recursUntil = null
      if (document.getElementById("weekly-recurring-checkbox").checked) {
        recurringFrequency = "Weekly"
        recursUntil = DateTime.fromJSDate(fpRecursUntil.selectedDates[0], {
          zone: util.TIMEZONE,
        }).toISODate()
        // Validate that weekly recurring blocks don't exceed 7 days
        if (endDateTime.diff(startDateTime, "days").days > 7) {
          displayBlockError(
            "time",
            "Weekly recurring blocks cannot be longer than 7 days. Please choose an earlier end date.",
            "Weekly Block Too Long"
          )
          return
        }
      }
      let buttonText = button.innerText
      button.innerText = "Please wait..."
      let token = await utillogin.getClerkToken()
      let endpoint
      let jsonBody = {
        token: token,
        start_utc: startDateTime.toUTC().toISO(),
        end_utc: endDateTime.toUTC().toISO(),
        recurring_frequency: recurringFrequency,
        recurs_until: recursUntil,
        space_ids: selectedSpaceIds,
        note: reason,
      }
      if (isUpdate) {
        endpoint = `update-block`
        jsonBody.block_id = parseInt(
          document.getElementById("update-block").dataset.blockId
        )
      } else {
        endpoint = `add-block`
        if (document.getElementById("select-organisation").value) {
          jsonBody.organisation_id = parseInt(
            document.getElementById("select-organisation").value
          )
        }
      }
      let responseData
      try {
        responseData = await util.api
          .post(endpoint, { json: jsonBody, retry: { limit: 4 } })
          .json()
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Unknown Error Saving Block")
      }
      button.innerText = buttonText
      if (responseData && responseData.success) {
        closeAddBlockSlideOver()
        loadCalendarData(util.getSearchParams().get("organisation"), false)
        let message = "The block has been added."
        if (isUpdate) {
          message = "The block has been updated."
        }
        Toastify({
          text: message,
          duration: 4000,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "#1aa979",
          },
        }).showToast()
      } else {
        displayBlockError(
          "time",
          "There was an error saving the block. Please try again, refreshing the page if necessary. If it still fails please email venuesupport@sharesy.com",
          "Save Block Failed"
        )
        return false
      }
      return true
    }

    async function deleteBlock(blockId) {
      let token = await utillogin.getClerkToken()
      let responseData
      try {
        responseData = await util.api
          .post("delete-block", {
            json: {
              token: token,
              block_id: blockId,
            },
            retry: { limit: 4 },
          })
          .json()
      } catch (error) {
        util.logToSentry(error)
        util.logAmplitudeEvent("Unknown Error Deleting Block")
      }
      if (responseData && responseData.success) {
        closeAddBlockSlideOver()
        loadCalendarData(util.getSearchParams().get("organisation"), false)
        Toastify({
          text: "The block has been deleted.",
          duration: 4000,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "#1aa979",
          },
        }).showToast()
      } else {
        document.getElementById("delete-block").innerText = "Delete block"
        document.getElementById("delete-block").disabled = false
        displayBlockError(
          "time",
          "There was an error deleting the block. Please try again, refreshing the page if necessary. If it still fails please email venuesupport@sharesy.com",
          "Delete Block Failed"
        )
      }
    }

    function init() {
      initChecks()

      try {
        document.getElementById("add-block").addEventListener(
          "click",
          function (event) {
            let startISOStr = null
            if (fpCalendar && fpCalendar.selectedDates.length > 0) {
              startISOStr = DateTime.fromJSDate(fpCalendar.selectedDates[0], {
                zone: util.TIMEZONE,
              }).toISODate()
            }
            openAddBlockSlideOver(null, startISOStr, startISOStr)
            util.logAmplitudeEvent("Open Add Block Slide Over")
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("add-block-close").addEventListener(
          "click",
          function (event) {
            closeAddBlockSlideOver()
            util.logAmplitudeEvent("Close Add Block Slide Over")
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("all-day-checkbox").addEventListener(
          "click",
          function (event) {
            if (this.checked) {
              document
                .getElementById("start-date-time")
                .querySelector(".choices").style.display = "none"
              document
                .getElementById("end-date-time")
                .querySelector(".choices").style.display = "none"
            } else {
              document
                .getElementById("start-date-time")
                .querySelector(".choices").style.display = "block"
              document
                .getElementById("end-date-time")
                .querySelector(".choices").style.display = "block"
            }
            util.logAmplitudeEvent("Check All Day (Add Block)", {
              Checked: this.checked,
            })
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("weekly-recurring-checkbox").addEventListener(
          "click",
          function (event) {
            if (this.checked) {
              document
                .getElementById("recurs-until-section")
                .classList.remove("hide")
            } else {
              document
                .getElementById("recurs-until-section")
                .classList.add("hide")
            }
            util.logAmplitudeEvent("Check Weekly Recurring Block", {
              Checked: this.checked,
            })
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("all-spaces-checkbox").addEventListener(
          "click",
          function (event) {
            if (this.checked) {
              document
                .querySelectorAll('input[id^="space-checkbox-"]')
                .forEach(function (d) {
                  d.checked = true
                  d.dispatchEvent(new Event("sharesy-color-update"))
                })
              hideBlockError("space")
            } else {
              document
                .querySelectorAll('input[id^="space-checkbox-"]')
                .forEach(function (d) {
                  d.checked = false
                  d.dispatchEvent(new Event("sharesy-color-update"))
                })
            }
            util.logAmplitudeEvent("Check All Spaces (Add Block)", {
              Checked: this.checked,
            })
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("nav-all-spaces-checkbox").addEventListener(
          "click",
          function (event) {
            if (this.checked) {
              document
                .querySelectorAll('input[id^="nav-space-checkbox-"]')
                .forEach(function (d) {
                  d.checked = true
                  d.dispatchEvent(new Event("sharesy-color-update"))
                })
              hideBlockError("space")
            } else {
              document
                .querySelectorAll('input[id^="nav-space-checkbox-"]')
                .forEach(function (d) {
                  d.checked = false
                  d.dispatchEvent(new Event("sharesy-color-update"))
                })
            }
            if (calendarData) {
              // If the space changes we destroy the calendar to reset
              // business hours. Don't update before calendarData loads.
              updateCalendars(true)
            }
            util.logAmplitudeEvent("Check All Spaces (Calendar Nav)", {
              Checked: this.checked,
            })
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("block-reason").addEventListener(
          "input",
          function (event) {
            hideBlockError("reason")
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("start-time").addEventListener(
          "change",
          function (event) {
            hideBlockError("time")
          },
          false
        )
        document.getElementById("end-time").addEventListener(
          "change",
          function (event) {
            hideBlockError("time")
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("add-block-button").addEventListener(
          "click",
          function (event) {
            if (addBlock(false, event.target)) {
              util.logAmplitudeEvent("Block Added")
            }
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("update-block").addEventListener(
          "click",
          function (event) {
            if (addBlock(true, event.target)) {
              util.logAmplitudeEvent("Block Updated")
            }
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("delete-block").addEventListener(
          "click",
          function (event) {
            if (event.target.innerText === "Delete block") {
              event.target.innerText = "Are you sure?"
              util.logAmplitudeEvent("Delete Block Clicked (Update Block)")
            } else {
              event.target.innerText = "Please wait..."
              deleteBlock(
                document.getElementById("update-block").dataset.blockId
              )
            }
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("copy-block").addEventListener(
          "click",
          function (event) {
            copyBlock(document.getElementById("update-block").dataset.blockId)
            util.logAmplitudeEvent("Copy Block")
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("session-close").addEventListener(
          "click",
          function () {
            utildashboard.closeSessionSlideOver()
            util.logAmplitudeEvent("Close Session Slide Over (Calendar)")
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("edit-button").addEventListener(
          "click",
          function () {
            util.logAmplitudeEvent("Edit Session Clicked (Calendar)")
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("cancel-button")
          .addEventListener("click", async function () {
            if (
              document
                .getElementById("cancel-button")
                .innerText.startsWith("Cancel")
            ) {
              utildashboard.cancelSession(selectedSessionId, isAdmin)
              util.logAmplitudeEvent("Cancelled Session (Calendar)")
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("booker-all-sessions").addEventListener(
          "click",
          function () {
            util.logAmplitudeEvent("See User Bookings (Calendar)")
            let encodedEmail = encodeURIComponent(
              document.getElementById("booker-email").innerText
            )
            window.location.href = `/bookings?filter=${encodedEmail}`
          },
          false
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        if ("clipboard" in navigator) {
          document.getElementById("copy-booking-link").addEventListener(
            "click",
            async function () {
              util.logAmplitudeEvent("Copy Booking Link (Calendar)")
              let encodedEmail = encodeURIComponent(
                document.getElementById("booker-email").innerText
              )
              let url = `${
                window.location.origin
              }/bookings?filter=${encodedEmail}%20${document
                .getElementById("booking-session-id")
                .innerText.replace("-", "%20")}`
              await navigator.clipboard.writeText(url)
              document.getElementById("copy-booking-link").classList.add("hide")
              document
                .getElementById("booking-link-copied")
                .classList.remove("hide")
            },
            false
          )
        } else {
          document.getElementById("copy-booking-link").classList.add("hide")
        }
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        utildashboard.registerSpaceBookingLinkListener(
          "Copy Space Booking Link (Calendar)"
        )
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("slide-over-container")
          .addEventListener("click", (e) => {
            if (e.target.id === "slide-over-container") {
              closeAddBlockSlideOver()
              utildashboard.closeSessionSlideOver()
              util.logAmplitudeEvent("Close Slide Overs - Click Outside")
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }
    }

    return {
      init: init,
    }
  })()
}
